import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type NotificationEntity = {
  id?: number | string;
  name?: string | null;
  description?: string | null;
  created_at?: string | null;
  is_read?: boolean | number | null;
  // allow extra fields
  [key: string]: unknown;
};

export type NotificationsState = {
  items: NotificationEntity[];
  /** Best-effort unread count (derived on insert if possible). */
  unreadCount: number;
};

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
};

function normalizeUnreadFlag(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  return null;
}

function recomputeUnreadCount(items: NotificationEntity[]): number {
  let c = 0;
  for (const n of items) {
    const isRead = normalizeUnreadFlag(n.is_read);
    if (isRead === false) c += 1;
  }
  return c;
}

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setNotifications(state, action: PayloadAction<NotificationEntity[]>) {
      state.items = action.payload ?? [];
      state.unreadCount = recomputeUnreadCount(state.items);
    },

    pushNotification(state, action: PayloadAction<NotificationEntity>) {
      const n = action.payload ?? {};
      state.items = [n, ...state.items];

      // Best-effort update unread count without full recompute when possible.
      const isRead = normalizeUnreadFlag(n.is_read);
      if (isRead === false) state.unreadCount += 1;
      else state.unreadCount = recomputeUnreadCount(state.items);
    },

    markAllRead(state) {
      state.items = state.items.map((n) => ({ ...n, is_read: true }));
      state.unreadCount = 0;
    },
  },
});

export const { setNotifications, pushNotification, markAllRead } =
  notificationsSlice.actions;

export default notificationsSlice.reducer;